import React, { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { SingleValue } from 'react-select';
import './SearchableSelect.scss';

export interface SearchableSelectOption<V extends string | number = string | number> {
  value: V;
  label: string;
  isDisabled?: boolean;
}

export interface SearchableSelectProps<V extends string | number = string | number> {
  options: SearchableSelectOption<V>[];
  value: V | null | undefined;
  onChange: (value: V | null) => void;
  label?: string;
  name?: string;
  id?: string;
  placeholder?: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  /** Show an "x" to clear the selection. */
  clearable?: boolean;
  className?: string;
}

// Case- and accent-insensitive, so "anejo" finds "Añejo".
const normalize = (text: string) =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Single select with type-to-filter inside the select itself (react-select),
 * laid out like FormField (label, error, helper text) so it can replace a
 * plain FormField select anywhere the option list gets long.
 */
function SearchableSelect<V extends string | number = string | number>({
  options,
  value,
  onChange,
  label,
  name,
  id,
  placeholder,
  helperText,
  error,
  required = false,
  disabled = false,
  isLoading = false,
  clearable = false,
  className = '',
}: SearchableSelectProps<V>) {
  const { t } = useTranslation();
  const generatedId = useId();
  const fieldId = id || `searchable-select-${generatedId}`;
  const errorId = `${fieldId}-error`;
  const helperId = `${fieldId}-helper`;

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value]
  );

  return (
    <div className={`form-field searchable-select ${error ? 'searchable-select--error' : ''} ${className}`}>
      {label && (
        <label htmlFor={fieldId} className="form-field__label">
          {label}
          {required && <span className="form-field__required">*</span>}
        </label>
      )}

      <Select<SearchableSelectOption<V>, false>
        inputId={fieldId}
        name={name}
        classNamePrefix="searchable-select"
        options={options}
        value={selected}
        onChange={(option: SingleValue<SearchableSelectOption<V>>) => onChange(option ? option.value : null)}
        placeholder={placeholder ?? t('searchableSelect.placeholder')}
        noOptionsMessage={() => t('searchableSelect.noOptions')}
        loadingMessage={() => t('common.loading')}
        filterOption={(option: { label: string }, input: string) => normalize(option.label).includes(normalize(input))}
        isDisabled={disabled}
        isLoading={isLoading}
        isClearable={clearable}
        aria-invalid={!!error}
        aria-describedby={[error ? errorId : '', helperText ? helperId : ''].filter(Boolean).join(' ') || undefined}
        // Render the menu at body level so modals/cards with overflow don't clip it.
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
      />

      {error && (
        <div id={errorId} className="form-field__error" role="alert">
          {error}
        </div>
      )}

      {helperText && !error && (
        <div id={helperId} className="form-field__helper">
          {helperText}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
